import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchProjects = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const createProject = async (projectData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/projects`, projectData);
      await fetchProjects();
      return response.data;
    } catch (err) {
      setError(err.message);
      console.error('Error creating project:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateProject = async (projectId, updateData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.put(`${API}/projects/${projectId}`, updateData);
      await fetchProjects();
      return response.data;
    } catch (err) {
      setError(err.message);
      console.error('Error updating project:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteProject = async (projectId) => {
    setLoading(true);
    setError(null);
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      await fetchProjects();
    } catch (err) {
      setError(err.message);
      console.error('Error deleting project:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const exportProject = async (projectId) => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/export`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (err) {
      console.error('Error exporting project:', err);
      throw err;
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  return {
    projects,
    loading,
    error,
    fetchProjects,
    createProject,
    updateProject,
    deleteProject,
    exportProject,
  };
};

export const useProject = (projectId) => {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchProject = async () => {
    if (!projectId) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/projects/${projectId}`);
      setProject(response.data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching project:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateProject = async (updateData) => {
    if (!projectId) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await axios.put(`${API}/projects/${projectId}`, updateData);
      setProject(response.data);
      return response.data;
    } catch (err) {
      setError(err.message);
      console.error('Error updating project:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  return {
    project,
    loading,
    error,
    fetchProject,
    updateProject,
  };
};
